import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import {
  CloudUploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileTextOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  deleteAffiliateDocument,
  downloadAffiliateDocument,
  getAffiliateDocuments,
  uploadAffiliateDocument,
} from "../../repository/data.repository";
import { AffiliateDocument } from "../../repository/data.default";
import { useSelector } from "../../store/store";
import { authorizeSelectors } from "../../store/authorize";
import { getProblemMessage, retry } from "../../utils/http";

export interface AffiliateDocumentsPanelProps {
  // `uid` matches the shape used by the sibling user-card panels
  // (UserCompanyAssignmentsPanel / UserMetricsPanel).
  readonly uid: string | undefined;
  readonly open: boolean;
}

// Admin brief 2026-11-04 — supporting documents on Consultants/Collaborators.
// Upload + delete are admin-only (backend already gates by platform role);
// this panel also hides the Upload / Delete buttons for non-admins so the
// affiliate's own view is read-only.
function AffiliateDocumentsPanel(props: AffiliateDocumentsPanelProps) {
  const permissions = useSelector(authorizeSelectors.permissions);
  // Admin-only upload. We reuse the same permission scope as the Consultant/
  // Collaborator create path — if you can create one, you can manage docs.
  const canManage =
    permissions.can("PlatformConsultants", "create") ||
    permissions.can("PlatformCollaborators", "create");

  const [docs, setDocs] = useState<AffiliateDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = useCallback(() => {
    if (!props.uid) return;
    setLoading(true);
    retry(() => getAffiliateDocuments(props.uid!))
      .then((d) => setDocs(d ?? []))
      .catch((err) =>
        message.error(getProblemMessage(err) || "Failed to load documents."),
      )
      .finally(() => setLoading(false));
  }, [props.uid]);

  useEffect(() => {
    if (props.open && props.uid) load();
  }, [props.open, props.uid, load]);

  async function onDownload(row: AffiliateDocument): Promise<void> {
    try {
      await downloadAffiliateDocument(
        row.uid,
        `${row.fileName}${row.fileExtension}`,
      );
    } catch (err) {
      message.error(getProblemMessage(err) || "Failed to download.");
    }
  }

  async function onDelete(uid: string): Promise<void> {
    try {
      await deleteAffiliateDocument(uid);
      message.success("Document archived.");
      load();
    } catch (err) {
      message.error(getProblemMessage(err) || "Failed to archive.");
    }
  }

  if (!props.uid) return null;

  return (
    <Card
      size="small"
      title={
        <Space>
          <FileTextOutlined />
          <Typography.Text strong>Supporting documents</Typography.Text>
          <Typography.Text type="secondary">({docs.length})</Typography.Text>
        </Space>
      }
      extra={
        canManage && (
          <Button
            size="small"
            type="primary"
            icon={<CloudUploadOutlined />}
            onClick={() => setUploadOpen(true)}
          >
            Upload document
          </Button>
        )
      }
    >
      <Table
        size="small"
        rowKey={(r) => r.uid}
        dataSource={docs}
        loading={loading}
        pagination={docs.length > 10 ? { pageSize: 10, size: "small" } : false}
        locale={{
          emptyText: canManage
            ? "No documents yet. Click Upload document to add one."
            : "No documents on file.",
        }}
        columns={[
          {
            title: "File",
            dataIndex: "fileName",
            render: (v: string, row: AffiliateDocument) => (
              <span>
                {v}
                <Typography.Text type="secondary"> {row.fileExtension}</Typography.Text>
              </span>
            ),
          },
          {
            title: "Type",
            dataIndex: "documentType",
            width: 140,
            render: (v: string | undefined) =>
              v ? <Tag>{v}</Tag> : <Typography.Text type="secondary">—</Typography.Text>,
          },
          {
            title: "Size",
            dataIndex: "fileSize",
            width: 90,
            render: (v: number) => formatBytes(v),
          },
          {
            title: "Uploaded",
            dataIndex: "createdOn",
            width: 140,
            render: (v?: string) =>
              v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—",
          },
          {
            title: "",
            key: "actions",
            width: canManage ? 150 : 100,
            render: (_: any, row: AffiliateDocument) => (
              <Space size={4}>
                <Button
                  size="small"
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() => onDownload(row)}
                >
                  Download
                </Button>
                {canManage && (
                  <Popconfirm
                    title="Archive this document?"
                    description="It will be hidden from the list."
                    okText="Archive"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => onDelete(row.uid)}
                  >
                    <Button
                      size="small"
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                    />
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />
      <UploadDialog
        open={uploadOpen}
        uid={props.uid}
        onCancel={() => setUploadOpen(false)}
        onUploaded={() => {
          setUploadOpen(false);
          load();
        }}
      />
    </Card>
  );
}

function UploadDialog(props: {
  open: boolean;
  uid: string;
  onCancel: () => void;
  onUploaded: () => void;
}) {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!props.open) {
      form.resetFields();
      setFileList([]);
    }
  }, [props.open, form]);

  async function onSubmit(): Promise<void> {
    try {
      const v = await form.validateFields();
      if (!fileList.length || !fileList[0].originFileObj) {
        message.warning("Please pick a file to upload.");
        return;
      }
      setSaving(true);
      await uploadAffiliateDocument(
        props.uid,
        fileList[0].originFileObj as File,
        v.documentType,
        v.notes,
      );
      message.success("Document uploaded.");
      setSaving(false);
      props.onUploaded();
    } catch (err: any) {
      setSaving(false);
      if (err?.errorFields) return;
      message.error(getProblemMessage(err) || "Failed to upload.");
    }
  }

  return (
    <Modal
      open={props.open}
      title="Upload document"
      okText="Upload"
      onCancel={props.onCancel}
      onOk={onSubmit}
      confirmLoading={saving}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="File"
          required
          tooltip="PDF / Word / Excel / Image / text — up to 10 MB."
        >
          <Upload.Dragger
            maxCount={1}
            fileList={fileList}
            beforeUpload={(f) => {
              // Audit fix 10 (2026-11-04) — UI promised "Max 10 MB" but
              // nothing enforced it; large files still POSTed and the
              // gateway returned a 413. We now stop the upload before it
              // leaves the client and show a friendly message.
              const mb = f.size / 1024 / 1024;
              if (mb > 10) {
                message.error(`"${f.name}" is ${mb.toFixed(1)} MB — the limit is 10 MB.`);
                return Upload.LIST_IGNORE as any;
              }
              return false;
            }}
            onChange={(info) => setFileList(info.fileList.slice(-1))}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt,.csv"
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag file here to upload</p>
            <p className="ant-upload-hint">Max 10 MB.</p>
          </Upload.Dragger>
        </Form.Item>
        <Form.Item
          name="documentType"
          label="Document type"
          tooltip="Free-form label — e.g. CV, Certificate, Insurance, ID."
        >
          <Input placeholder="e.g. CV, Certificate, Insurance…" />
        </Form.Item>
        <Form.Item name="notes" label="Notes (optional)">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function formatBytes(n: number): string {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default AffiliateDocumentsPanel;
